import api from './api';
import { withAuditLog, ACTION_TYPES } from './auditLogService';

// Admin API endpoints
export const devAdminAPI = {
  // Get all users (admin list endpoint)
  getUsers: (params = {}) => api.get('/api/v1/admin/users', { params }),

  // Get reported users (users who have reports against them)
  getReportedUsers: (params = {}) => api.get('/api/v1/admin/users/reported', { params }),

  // Get all reports for a specific user
  getUserReports: (userId) => api.get(`/api/v1/admin/users/${userId}/reports`),

  // Moderation actions
  suspendUser: withAuditLog(
    ACTION_TYPES.USER_SUSPEND.key,
    (userId, { durationHours, reasons = [], reason = '', images = [], targetName } = {}) => {
      const formData = new FormData();
      formData.append('duration_hours', durationHours);
      if (reasons.length > 0) reasons.forEach(r => formData.append('reasons[]', r));
      if (reason) formData.append('reason', reason);
      images.forEach(file => formData.append('images[]', file));
      return api.post(`/api/v1/admin/users/${userId}/suspend`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    (userId, data) => ({
      targetId: userId,
      targetType: 'user',
      targetName: data?.targetName,
      details: {
        duration_hours: data?.durationHours,
        reasons: data?.reasons,
        reason: data?.reason,
      },
    })
  ),

  muteUser: withAuditLog(
    ACTION_TYPES.USER_MUTE.key,
    (userId, { durationHours, reasons = [], reason = '', images = [], targetName } = {}) => {
      const formData = new FormData();
      formData.append('duration_hours', durationHours);
      if (reasons.length > 0) reasons.forEach(r => formData.append('reasons[]', r));
      if (reason) formData.append('reason', reason);
      images.forEach(file => formData.append('images[]', file));
      return api.post(`/api/v1/admin/users/${userId}/mute`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    (userId, data) => ({
      targetId: userId,
      targetType: 'user',
      targetName: data?.targetName,
      details: {
        duration_hours: data?.durationHours,
        reasons: data?.reasons,
        reason: data?.reason,
      },
    })
  ),

  banUser: withAuditLog(
    ACTION_TYPES.USER_BAN.key,
    (userId, { reasons = [], reason = '', images = [], targetName } = {}) => {
      const formData = new FormData();
      if (reasons.length > 0) reasons.forEach(r => formData.append('reasons[]', r));
      if (reason) formData.append('reason', reason);
      images.forEach(file => formData.append('images[]', file));
      return api.post(`/api/v1/admin/users/${userId}/ban`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    (userId, data) => ({
      targetId: userId,
      targetType: 'user',
      targetName: data?.targetName,
      details: {
        reasons: data?.reasons,
        reason: data?.reason,
      },
    })
  ),

  unsuspendUser: withAuditLog(
    ACTION_TYPES.USER_UNSUSPEND.key,
    (userId, { targetName } = {}) => api.post(`/api/v1/admin/users/${userId}/unsuspend`),
    (userId, data) => ({ targetId: userId, targetType: 'user', targetName: data?.targetName })
  ),

  unmuteUser: withAuditLog(
    ACTION_TYPES.USER_UNMUTE.key,
    (userId, { targetName } = {}) => api.post(`/api/v1/admin/users/${userId}/unmute`),
    (userId, data) => ({ targetId: userId, targetType: 'user', targetName: data?.targetName })
  ),

  ignoreUserReports: withAuditLog(
    ACTION_TYPES.USER_IGNORE_REPORTS.key,
    (userId, { targetName } = {}) => api.post(`/api/v1/admin/users/${userId}/reports/ignore`),
    (userId, data) => ({ targetId: userId, targetType: 'user', targetName: data?.targetName })
  ),

  ignorePostReports: withAuditLog(
    ACTION_TYPES.POST_IGNORE_REPORTS.key,
    (postId) => api.post(`/api/v1/admin/posts/${postId}/reports/ignore`),
    (postId) => ({ targetId: postId, targetType: 'post' })
  ),

  // Suspended / Muted / Banned users
  getSuspendedUsers: (params = {}) => api.get('/api/v1/admin/users/suspended', { params }),
  getMutedUsers: (params = {}) => api.get('/api/v1/admin/users/muted', { params }),
  getBannedUsers: (params = {}) => api.get('/api/v1/admin/users/banned', { params }),

  restoreUser: withAuditLog(
    ACTION_TYPES.USER_RESTORE.key,
    (userId, { targetName } = {}) => api.post(`/api/v1/admin/users/${userId}/restore`),
    (userId, data) => ({ targetId: userId, targetType: 'user', targetName: data?.targetName })
  ),

  // Get only end-user types (guardians, vets, service providers, firms)
  getEndUsers: (params = {}) => api.get('/api/v1/admin/users', {
    params: { types: 'PET_GUARDIAN,VETERINARIAN,SERVICE_PROVIDER,FIRM', ...params }
  }),

  // Get single user details
  getUserDetails: (userId) => api.get(`/api/v1/admin/users/${userId}`),

  // Delete user (soft delete with backend cascade)
  deleteUser: withAuditLog(
    ACTION_TYPES.USER_DELETE.key,
    (userId, { targetName } = {}) => api.delete(`/api/v1/admin/users/${userId}`),
    (userId, data) => ({ targetId: userId, targetType: 'user', targetName: data?.targetName })
  ),

  // Permanently delete user (force delete with backend cascade) — legacy, use banUser instead
  permanentDeleteUser: withAuditLog(
    ACTION_TYPES.USER_PERMANENT_DELETE.key,
    (userId, { targetName } = {}) => api.delete(`/api/v1/admin/users/${userId}/permanent`),
    (userId, data) => ({ targetId: userId, targetType: 'user', targetName: data?.targetName })
  ),

  // Delete pet (using specific admin endpoint for permission bypass)
  deletePet: withAuditLog(
    ACTION_TYPES.PET_DELETE.key,
    (petId) => api.delete(`/api/v1/admin/pets/${petId}`),
    (petId) => ({ targetId: petId, targetType: 'pet' })
  ),

  // Get active communities (optional, for future dashboard widgets)
  getActiveCommunities: () => api.get('/api/v1/admin/communities/active'),

  // Post moderation
  getPosts: (params = {}) => api.get('/api/v1/admin/posts', { params }),
  getPostDetails: (postId) => api.get(`/api/v1/admin/posts/${postId}`),

  deletePost: withAuditLog(
    ACTION_TYPES.POST_DELETE.key,
    (postId, { reasons = [], reason = null, images = [] } = {}) => {
      const formData = new FormData();
      if (reasons.length > 0) reasons.forEach(r => formData.append('reasons[]', r));
      if (reason) formData.append('reason', reason);
      images.forEach(file => formData.append('images[]', file));
      return api.post(`/api/v1/admin/posts/${postId}/delete`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    (postId, data) => ({
      targetId: postId,
      targetType: 'post',
      details: {
        reasons: data?.reasons,
        reason: data?.reason,
      },
    })
  ),
};

// Data Entry API (dropdown-only species & breeds, separate from encyclopedia)
export const dataEntryAPI = {
  // Get all species for dropdowns/data-entry (including dropdown-only ones)
  getSpeciesList: (params = {}) => api.get('/api/v1/species', { params: { all: true, ...params } }),

  // Create a dropdown-only species
  createSpecies: (data) => api.post('/api/v1/data-entry/species', data),

  // Update a species
  updateSpecies: (speciesId, data) => api.put(`/api/v1/data-entry/species/${speciesId}`, data),

  // Delete a species
  deleteSpecies: (speciesId) => api.delete(`/api/v1/data-entry/species/${speciesId}`),

  // Get breed types for a species (dropdown data)
  getBreedsBySpecies: (speciesId) => api.get(`/api/v1/breed-types/species/${speciesId}`, { params: { include_all: true } }),

  // Create a dropdown-only breed type
  createBreed: (data) => api.post('/api/v1/data-entry/breed-types', data),

  // Update a breed type
  updateBreed: (breedId, data) => api.put(`/api/v1/data-entry/breed-types/${breedId}`, data),

  // Delete a breed type
  deleteBreed: (breedId) => api.delete(`/api/v1/data-entry/breed-types/${breedId}`),
};
